import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Circle,
  Image,
  Flex,
  useDisclosure
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { motion } from 'framer-motion';
import { FaCoins } from 'react-icons/fa';
import { RiCoinsFill } from 'react-icons/ri';

/**
 * BaseCardComponent
 * 
 * A flexible base component for scratch cards that provides consistent layout and behavior.
 * Individual card types can extend this component with their specific styling and configuration.
 */
const BaseCardComponent = ({ 
  config,
  isPreview = false, 
  onPlay,
  onBuy,
  gameState = null,
  customRenderCell = null,
  customSymbol = '⭐',
  gridSymbol = null, // Separate prop for grid symbol
  renderSymbol = null, // Function to render symbols (handles images)
  onReveal = null
}) => {
  // Get prize values from gameState or use config prizes as fallback
  const prizeValues = gameState?.prizeValues || config.prizes || ['200', '1K', '5K', '10K', '50K', '100K'];
  
  // Get winning symbols directly from gameState if available
  const winningSymbols = gameState?.winningSymbols || prizeValues;
  const totalCells = config.gridSize.rows * config.gridSize.columns;
  
  // State for cells in the grid
  const [cells, setCells] = useState(
    Array(totalCells).fill().map((_, i) => ({ 
      id: i, 
      revealed: false, // Don't auto-reveal in preview mode
      value: gridSymbol, // Initialize with the gridSymbol passed as prop
      isWinning: false,
      isMatch: false // Track if this cell is part of a matching set
    }))
  );
  
  // Debug the symbols being passed
  console.log('Custom Symbol:', customSymbol);
  console.log('Grid Symbol:', gridSymbol);
  
  // Update cells when gameState changes
  useEffect(() => {
    if (gameState?.cells) {
      setCells(gameState.cells);
    }
  }, [gameState?.cells]);
  
  // Track matched values
  const [matchedValue, setMatchedValue] = useState(null);
  
  // State for winning symbols reveal - in preview mode, we don't animate
  const [winningSymbolsRevealed, setWinningSymbolsRevealed] = useState(false);
  
  // Animation state for winning symbols
  const [animatingSymbols, setAnimatingSymbols] = useState(false);
  
  // Calculate scaling factors based on grid dimensions
  const { rows, columns } = config.gridSize;
  const fontScaleFactor = Math.max(0.7, 9 / totalCells); // Scale down fonts for larger grids
  
  // In preview mode, we want to show a static representation of the card
  // with consistent styling that matches the initial game state
  const initialRender = isPreview;
  
  // Check for matching values in the revealed cells
  useEffect(() => {
    if (isPreview || !winningSymbolsRevealed) return;
    
    // Count occurrences of each value in the revealed cells
    const valueCounts = {};
    const revealedValues = {};
    
    cells.forEach(cell => {
      if (cell.revealed && cell.value) {
        valueCounts[cell.value] = (valueCounts[cell.value] || 0) + 1;
        if (!revealedValues[cell.value]) {
          revealedValues[cell.value] = [];
        }
        revealedValues[cell.value].push(cell.id);
      }
    });
    
    // Check if any value appears 3 or more times and is in the prize values
    let foundMatch = false;
    for (const [value, count] of Object.entries(valueCounts)) {
      if (count >= 3 && prizeValues.includes(value)) {
        // Found a matching set
        foundMatch = true;
        setMatchedValue(value);
        
        // Mark these cells as matches
        const newCells = [...cells];
        revealedValues[value].forEach(cellId => {
          newCells[cellId].isMatch = true;
        });
        setCells(newCells);
        break;
      }
    }
    
    if (!foundMatch) {
      setMatchedValue(null);
    }
  }, [cells, prizeValues, isPreview, winningSymbolsRevealed]);
  
  // Listen for changes in gameState to update cells and winning symbols
  useEffect(() => {
    // Handle normal gameState updates
    if (gameState) {
      // Check if winning symbols should be revealed
      if (gameState.winningSymbolsRevealed && !winningSymbolsRevealed) {
        // Animate the winning symbols reveal
        setAnimatingSymbols(true);
        setTimeout(() => {
          setWinningSymbolsRevealed(true);
          setAnimatingSymbols(false);
        }, 1000); // Animation duration
      }
      
      // Update cells based on gameState
      if (gameState.revealedCells) {
        const newCells = [...cells];
        
        // Mark cells as revealed based on gameState.revealedCells
        gameState.revealedCells.forEach(index => {
          if (index >= 0 && index < newCells.length) {
            newCells[index].revealed = true;
            
            // Set cell value and winning status
            if (gameState.cellValues && !newCells[index].value) {
              newCells[index].value = gameState.cellValues[index];
              newCells[index].isWinning = gameState.winningCells?.includes(index);
            }
          }
        });
        
        setCells(newCells);
      }
    }
  }, [gameState, isPreview, totalCells]);
  
  // Handle cell click (scratch)
  const handleCellClick = (index) => {
    // Don't allow revealing cells if winning symbols aren't revealed yet
    if (isPreview || !gameState || gameState.status !== 'active' || !winningSymbolsRevealed) return;
    
    const newCells = [...cells];
    newCells[index].revealed = true;
    
    // In a real game, this would come from the backend
    if (!newCells[index].value && gameState.cellValues) {
      newCells[index].value = gameState.cellValues[index];
      newCells[index].isWinning = gameState.winningCells?.includes(index);
    }
    
    setCells(newCells);
    
    // Call the onReveal callback if provided
    if (onReveal) {
      onReveal(index, newCells[index].value, newCells[index].isWinning);
    }
  };
  
  // Function to reveal all cells at once
  const revealAllCells = () => {
    // Don't allow revealing cells if winning symbols aren't revealed yet
    if (isPreview || !gameState || gameState.status !== 'active' || !winningSymbolsRevealed) return;
    
    const newCells = [...cells];
    
    // Reveal all cells with a staggered animation effect
    for (let i = 0; i < totalCells; i++) {
      setTimeout(() => {
        setCells(prev => {
          const updated = [...prev];
          updated[i].revealed = true;
          
          // Set cell value and winning status
          if (!updated[i].value && gameState.cellValues) {
            updated[i].value = gameState.cellValues[i];
            updated[i].isWinning = gameState.winningCells?.includes(i);
          }
          
          return updated;
        });
        
        // Call onReveal for the last cell to trigger win detection
        if (i === totalCells - 1 && onReveal) {
          onReveal(i, gameState.cellValues[i], gameState.winningCells?.includes(i), true);
        }
      }, i * 100); // Stagger the reveals by 100ms each
    }
  };
  
  // Expose the revealAllCells function to parent components
  useEffect(() => {
    if (gameState && gameState.revealAll) {
      revealAllCells();
    }
  }, [gameState?.revealAll]);
  
  // Cell renderer with animation effects
  const renderCell = (index) => {
    // Guard against undefined cells
    if (!cells || !cells[index]) {
      return null;
    }
    
    const cell = cells[index];
    const isRevealed = cell?.revealed || false; // Don't auto-reveal in preview mode
    
    // If custom render function is provided and returns a non-null value, use it
    if (customRenderCell) {
      const customCell = customRenderCell(index, cell, isRevealed);
      if (customCell) return customCell;
    }
    
    // Animation for cell reveal
    const revealAnimation = keyframes`
      0% { transform: rotateY(0deg); }
      50% { transform: rotateY(90deg); }
      100% { transform: rotateY(0deg); }
    `;
    
    // Pulsing animation for matching cells - recreated when config changes
    const pulseAnimation = keyframes`
      0% { transform: scale(1); box-shadow: 0 0 5px ${config.accentColor || "#3182CE"}; }
      25% { transform: scale(1.08); box-shadow: 0 0 15px ${config.accentColor || "#3182CE"}; }
      50% { transform: scale(1.05); box-shadow: 0 0 20px ${config.accentColor || "#3182CE"}, 0 0 40px ${config.accentColor || "#3182CE"}; }
      75% { transform: scale(1.08); box-shadow: 0 0 15px ${config.accentColor || "#3182CE"}; }
      100% { transform: scale(1); box-shadow: 0 0 5px ${config.accentColor || "#3182CE"}; }
    `;
    
    // Shine effect for matching cells
    const shineAnimation = keyframes`
      0% { background-position: -100% 0; }
      100% { background-position: 200% 0; }
    `;
    
    // Default cell rendering with animation
    return (
      <Box
        key={index}
        bg={cell.isWinning && isRevealed ? (config.winningSymbolBackground || "#FFD700") : (config.gridBoxBackground || config.symbolBackground || (config.id === 'quick_cash' ? "#0a1f3d" : (config.accentColor ? `${config.accentColor}40` : "#ffc107")))} // Use specific colors for winning and normal cells
        border="1px solid"
        borderColor={cell.isWinning && isRevealed ? (config.winningSymbolBackground || config.accentColor || "gold") : (config.gridBoxBackground || (config.bgColor ? `${config.bgColor}80` : "#e6a800"))}
        borderRadius="md"
        height="80px"
        width="80px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        cursor={!isPreview && !isRevealed && winningSymbolsRevealed ? "pointer" : "default"}
        onClick={() => handleCellClick(index)}
        position="relative"
        transition="all 0.2s"
        _hover={!isPreview && !isRevealed && winningSymbolsRevealed ? { transform: "scale(1.05)" } : {}}
        m={0}
        boxShadow={cell.isMatch && isRevealed ? `0 0 15px ${config.accentColor || "#3182CE"}, 0 0 30px ${config.accentColor || "#3182CE"}` : "none"}
        as={motion.div}
        border={cell.isMatch && isRevealed ? `2px solid ${config.accentColor || "#3182CE"}` : `1px solid ${config.bgColor ? `${config.bgColor}80` : "#e6a800"}`}
        sx={{
          animation: cell.isMatch && isRevealed ? `${pulseAnimation} 2s infinite` : (isRevealed && !isPreview ? `${revealAnimation} 0.5s forwards` : "none")
        }}
        style={{ perspective: "1000px" }}
      >
        {isRevealed ? (
          <Box
            width="80%"
            height="80%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color={cell.isWinning ? (config.winningSymbolPicture || config.accentColor || "gold") : (config.gridBoxPicture || config.bgColor || "white")}
            fontSize="xl"
            fontWeight="bold"
          >
            {cell.isMatch ? (
              <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="center" 
                flexDirection="column"
                position="relative"
                _after={{
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)`,
                  backgroundSize: '200% 100%',
                  animation: `${shineAnimation} 2s infinite linear`
                }}
              >
                <Text 
                  fontSize={`${Math.max(16, 24 * fontScaleFactor)}px`} 
                  color={config.winningSymbolPicture || config.bgColor || "blue.900"} 
                  fontWeight="bold" 
                  transform="rotateY(0deg)"
                >
                  {(() => {
                    // Check if the cell value is a URL to an image
                    if (cell.value && typeof cell.value === 'string' && (cell.value.startsWith('/images/') || cell.value.startsWith('http'))) {
                      return <Image src={cell.value} height="80%" width="80%" objectFit="contain" alt="Symbol" />
                    } else {
                      // Default to text/emoji symbol
                      return cell.value
                    }
                  })()}
                </Text>
                <Text 
                  fontSize={`${Math.max(10, 14 * fontScaleFactor)}px`} 
                  mt={fontScaleFactor > 0.8 ? 1 : 0} 
                  color={config.winningSymbolPicture || config.accentColor || "blue.400"} 
                  fontWeight="bold" 
                  transform="rotateY(0deg)"
                >
                  MATCH!
                </Text>
              </Box>
            ) : (
              <Text 
                fontSize={`${Math.max(16, 24 * fontScaleFactor)}px`} 
                color={config.gridBoxPicture || config.bgColor || "white"} 
                fontWeight="bold" 
                transform="rotateY(0deg)"
              >
                {(() => {
                  // Check if the cell value is a URL to an image
                  if (cell.value && typeof cell.value === 'string' && (cell.value.startsWith('/images/') || cell.value.startsWith('http'))) {
                    return <Image src={cell.value} height="80%" width="80%" objectFit="contain" alt="Symbol" />
                  } else {
                    // Default to text/emoji symbol
                    return cell.value || '0'
                  }
                })()}
              </Text>
            )}
          </Box>
        ) : (
          <Box 
            position="absolute" 
            top="0" 
            left="0" 
            right="0" 
            bottom="0" 
            bg={config.gridBoxBackground || (config.accentColor ? `${config.accentColor}80` : "#ffc107")}
            borderRadius="md"
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={1}
          >
            {/* Use the grid symbol or a predefined symbol instead of hardcoded image */}
            {(() => {
              // Check if the gridSymbol is a direct URL to an image (from NFT folder or uploaded)
              if (gridSymbol && typeof gridSymbol === 'string' && (gridSymbol.startsWith('/images/') || gridSymbol.startsWith('http'))) {
                return (
                  <Box position="relative" width="100%" height="100%" overflow="hidden">
                    <Image 
                      src={gridSymbol} 
                      position="absolute"
                      top="0"
                      left="0"
                      width="100%" 
                      height="100%" 
                      objectFit="cover" 
                      alt="Grid Symbol" 
                    />
                  </Box>
                );
              } else if (typeof (gridSymbol || customSymbol) === 'string') {
                // Handle text/emoji symbols
                return <Text fontSize="4xl" fontWeight="bold">{gridSymbol || customSymbol}</Text>;
              } else if (gridSymbol && gridSymbol.url) {
                // Handle custom uploaded images with URL property
                return (
                  <Box position="relative" width="100%" height="100%" overflow="hidden">
                    <Image 
                      src={gridSymbol.url} 
                      position="absolute"
                      top="0"
                      left="0"
                      width="100%" 
                      height="100%" 
                      objectFit="cover" 
                      alt="Custom Symbol" 
                    />
                  </Box>
                );
              } else {
                // Fallback to default image
                return <Image src="/images/777.png" width="90%" height="90%" objectFit="contain" alt="Default Symbol" />;
              }
            })()}  
          </Box>
        )}
      </Box>
    );
  };
  
  // Render the grid
  const renderGrid = () => {
    // Calculate grid dimensions based on config
    const { rows, columns } = config.gridSize;
    
    // Fixed values for consistent layout
    const edgePadding = 12; // Padding around the entire grid
    const gridGap = 8; // Gap between cells
    
    // Calculate container and cell sizes based on grid dimensions
    const calculateSizes = () => {
      // Fixed container width (inner content area, not including edge padding)
      const containerWidth = 280;
      
      // Calculate available space for cells after accounting for gaps
      const totalGapWidth = gridGap * (columns - 1);
      
      // Calculate cell dimensions to fit the container
      const cellWidth = Math.floor((containerWidth - totalGapWidth) / columns);
      
      // Make cells square
      const cellSize = cellWidth;
      
      // Calculate total grid width including edge padding
      const totalWidth = containerWidth + (edgePadding * 2);
      
      return { cellSize, containerWidth, totalWidth };
    };
    
    const { cellSize, containerWidth, totalWidth } = calculateSizes();
    
    return (
      <Box
        bg={config.gridBackground || (config.accentColor ? `${config.accentColor}80` : "rgba(20, 40, 80, 0.8)")}
        borderRadius="md"
        width="100%"
        maxW={`${totalWidth}px`}
        minH="300px"
        mx="auto"
        my={4}
        display="flex"
        justifyContent="center"
        alignItems="center"
        borderWidth="0.5px"
        borderColor="rgba(255, 255, 255, 0.1)"
        boxShadow="0 4px 12px rgba(0, 0, 0, 0.3), 0 0 10px 1px rgba(255, 255, 255, 0.03)"
        overflow="hidden"
        position="relative"
      >
        <Box
          width={`${containerWidth}px`}
          display="grid"
          gridTemplateColumns={`repeat(${columns}, ${cellSize}px)`}
          gridTemplateRows={`repeat(${rows}, ${cellSize}px)`}
          gap={`${gridGap}px`}
          justifyContent="center"
          alignItems="center"
          mx="auto"
          p={`${edgePadding}px`}
        >
          {Array(totalCells).fill().map((_, i) => (
            <Box 
              key={i} 
              display="flex" 
              justifyContent="center" 
              alignItems="center"
              width={`${cellSize}px`}
              height={`${cellSize}px`}
              borderRadius="sm"
              overflow="hidden"
              borderWidth="0.5px"
              borderColor="rgba(255, 255, 255, 0.08)"
              boxShadow="0 0 5px rgba(255, 255, 255, 0.02)"
            >
              {renderCell(i)}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };
  
  return (
    <Box 
      position="relative" 
      bg={config.cardBackground || (config.bgColor ? `${config.bgColor}F2` : (config.id === 'cash_king' ? "rgba(0, 30, 15, 0.95)" : "rgba(0, 15, 30, 0.95)"))} 
      p={8} 
      borderRadius="lg" 
      boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.5), 0 0 15px 2px rgba(255, 255, 255, 0.05)"
      borderWidth="0.5px"
      borderColor="rgba(255, 255, 255, 0.1)"
      backdropFilter="blur(10px)"
      width="400px"
      height="650px"
      maxW="100%"
      mx="0"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      overflow="hidden"
      {...(config.gradient && {
        bgGradient: config.gradient,
        bg: 'transparent'
      })}
    >
      {/* Cost badge */}
      <Box 
        position="absolute" 
        top="10px" 
        left="10px" 
        bg="rgba(0,0,0,0.7)" 
        color="yellow.300" 
        px={2} 
        py={1} 
        borderRadius="md"
        fontWeight="bold"
        display="flex"
        alignItems="center"
        gap={1}
      >
        <FaCoins /> {config.cost} {config.costCurrency || 'JINU'}
      </Box>
      
      {/* Creator badge if applicable */}
      <Box 
        position="absolute" 
        top="10px" 
        right="10px" 
        bg="rgba(0,0,0,0.7)" 
        color="purple.300" 
        px={2} 
        py={1} 
        borderRadius="md"
        fontWeight="bold"
        display="flex"
        alignItems="center"
        gap={1}
      >
        👑 {config.creator || 'PALMHEIST'}: 20%
      </Box>
      
      {/* Card header - Title and description */}
      <VStack spacing={1} mt={12} mb={2}>
        <Heading 
          color="yellow.300" 
          fontWeight="bold" 
          fontSize="3xl"
          textAlign="center"
        >
          {config.name}
        </Heading>
        <Text 
          color="white" 
          fontWeight="bold" 
          fontSize="md"
          textAlign="center"
        >
          {config.description}
        </Text>
      </VStack>
      
      {/* Winning Symbols Display with Animation */}
      <Box textAlign="center" mt={2} mb={3}>
        <HStack justify="center" spacing={3}>
          {Array.from({ length: config.gameLogic?.winningSymbolsCount || config.winningSymbols || 3 }).map((_, i) => {
            // Animation for symbol reveal
            const symbolRevealAnimation = keyframes`
              0% { transform: scale(0.5); opacity: 0; }
              60% { transform: scale(1.2); opacity: 1; }
              100% { transform: scale(1); opacity: 1; }
            `;
            
            // Flip animation for revealing prize values
            const flipAnimation = keyframes`
              0% { transform: rotateY(0deg); }
              100% { transform: rotateY(180deg); }
            `;
            
            // Select a winning symbol for this position
            const prizeValue = winningSymbols && winningSymbols[i] ? winningSymbols[i] : prizeValues[i % prizeValues.length];
            
            return (
              <Box
                key={i}
                position="relative"
                width="50px"
                height="50px"
                style={{ 
                  perspective: "1000px",
                  transformStyle: "preserve-3d"
                }}
                as={motion.div}
                animate={{
                  rotateY: winningSymbolsRevealed && !isPreview ? 180 : 0
                }}
                transition={{
                  duration: 0.8,
                  delay: i * 0.2,
                  ease: "easeInOut"
                }}
              >
                {/* Front side - Star */}
                <Circle 
                  position="absolute"
                  top="0"
                  left="0"
                  size="50px" 
                  bg={config.symbolBackground || "#0a1f3d"}
                  color={config.winningSymbolPicture || "yellow.300"}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  border="1px solid"
                  borderColor={config.winningSymbolPicture ? `${config.winningSymbolPicture}40` : "rgba(255, 255, 255, 0.2)"}
                  boxShadow="0 0 10px rgba(0,0,0,0.2)"
                  style={{
                    backfaceVisibility: "hidden",
                    transform: "rotateY(0deg)",
                    zIndex: winningSymbolsRevealed && !isPreview ? 0 : 2
                  }}
                >
                  {(() => {
                    // Check if the customSymbol is a direct URL to an image
                    if (customSymbol && typeof customSymbol === 'string' && (customSymbol.startsWith('/images/') || customSymbol.startsWith('http'))) {
                      return (
                        <Box position="relative" width="100%" height="100%" overflow="hidden" borderRadius="50%">
                          <Image 
                            src={customSymbol} 
                            position="absolute"
                            top="0"
                            left="0"
                            width="100%" 
                            height="100%" 
                            objectFit="cover" 
                            alt="Winning Symbol" 
                          />
                        </Box>
                      );
                    } else {
                      // Default to text/emoji symbol
                      return <Text fontSize="24px" fontWeight="bold">{customSymbol}</Text>;
                    }
                  })()} 
                </Circle>
                
                {/* Back side - Prize Value */}
                <Circle 
                  position="absolute"
                  top="0"
                  left="0"
                  size="50px" 
                  bg={config.winningSymbolBackground || "gold"}
                  color={config.gridBoxPicture || "#0a1f3d"}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  border="1px solid"
                  borderColor={config.winningSymbolBackground ? `${config.winningSymbolBackground}80` : "rgba(0, 0, 0, 0.2)"}
                  boxShadow={`0 0 10px ${config.winningSymbolBackground ? `${config.winningSymbolBackground}50` : "rgba(255, 215, 0, 0.5)"}`}
                  style={{
                    backfaceVisibility: "hidden",
                    transform: "rotateY(180deg)",
                    zIndex: winningSymbolsRevealed && !isPreview ? 2 : 0
                  }}
                >
                  <Text fontSize={`${Math.max(12, 16 * fontScaleFactor)}px`} fontWeight="bold">{prizeValue}</Text>
                </Circle>
              </Box>
            );
          })}
        </HStack>
        <Text 
          color={winningSymbolsRevealed ? "gold" : "#4fd1c5"} 
          mt={2} 
          mb={1} 
          fontSize="md" 
          fontWeight="medium"
          transition="color 0.5s"
        >
          Winning Symbols
        </Text>
      </Box>
      
      {/* Card grid */}
      {renderGrid()}
      
      {/* Match instructions */}
      <Text 
        color="white" 
        fontSize="sm"
        textAlign="center" 
        mt={2}
        mb={2}
        fontWeight="medium"
      >
        Match any of the winning symbols to win their prize value.
      </Text>
    </Box>
  );
};

export default BaseCardComponent;
